import { useRef, useState } from 'react';
import type { MouseEvent } from 'react';

import { reportUnhandledError } from '@/lib/errors/unhandledErrors';

import { displayCssClass, effectiveBaseCssClass, isIconOnly } from './ngButtonBase';
import type { NgButtonBaseProps } from './ngButtonBase';

export type NgButtonProps = NgButtonBaseProps & {
  disabled?: boolean;
  submitButton?: boolean;
  form?: string;
  title?: string;
  ariaLabel?: string;
  ariaHasPopup?: boolean;
  ariaExpanded?: boolean;
  ariaControls?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  confirmationMessage?: string;
  enableMultipleClickPrevention?: boolean;
  showProgress?: boolean;
  minimumProgressTimeOutMs?: number;
};

export function NgButton(props: NgButtonProps) {
  const {
    disabled,
    submitButton,
    form,
    title,
    ariaLabel,
    ariaHasPopup,
    ariaExpanded,
    ariaControls,
    onClick,
    confirmationMessage,
    enableMultipleClickPrevention,
    showProgress,
    minimumProgressTimeOutMs = 0,
    icon,
    text,
    children,
  } = props;

  const [isInProgress, setIsInProgress] = useState(false);
  const inProgressRef = useRef(false);

  const effectiveButtonType = submitButton ? 'submit' : 'button';
  const effectiveCssClass = `${displayCssClass(props)} ${effectiveBaseCssClass(props)}`;
  const showIcon = icon != null || (showProgress === true && isInProgress);
  const effectiveIcon =
    showProgress && isInProgress
      ? 'fal fa-spinner-third fa-spin fa-lg'
      : icon != null
        ? `${icon} fa-lg`
        : undefined;

  const buttonClicked = async (event: MouseEvent<HTMLButtonElement>) => {
    if (enableMultipleClickPrevention && inProgressRef.current) {
      return;
    }

    inProgressRef.current = true;
    setIsInProgress(true);

    const executionStart = Date.now();

    try {
      if (confirmationMessage == null || window.confirm(confirmationMessage)) {
        if (onClick) {
          await onClick(event);
        }
      }

      const elapsedTime = Date.now() - executionStart;
      if (elapsedTime < minimumProgressTimeOutMs) {
        await new Promise((resolve) => setTimeout(resolve, minimumProgressTimeOutMs - elapsedTime));
      }
    } catch (error) {
      console.error('Button click handler failed.', error);
      reportUnhandledError(error);
    } finally {
      inProgressRef.current = false;
      setIsInProgress(false);
    }
  };

  const screenReaderNameForIconOnlyButton = title;
  const effectiveAriaLabel = ariaLabel ?? (isIconOnly(props) ? screenReaderNameForIconOnlyButton : undefined);

  return (
    <button
      type={effectiveButtonType}
      className={effectiveCssClass}
      disabled={disabled}
      onClick={(event) => void buttonClicked(event)}
      title={title}
      form={form}
      aria-label={effectiveAriaLabel}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
    >
      {showIcon && <i className={effectiveIcon} aria-hidden="true"></i>}
      {children != null ? children : text}
    </button>
  );
}
